import InputWithSelect from "@/components/InputWithSelect";
import { IMaskInput } from "react-imask";
import { useTranslation } from "@/i18n/client";
import React, { useContext, useMemo } from "react";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { Country } from "@/types/enumerations";
import parsePhoneNumber from "libphonenumber-js";
import { FormInputProps } from "@/types";

const foreignAddressesFeatureFLag = process.env.NEXT_PUBLIC_FOREIGN_ADDRESS_FEATURE_FLAG === "true";

const getParsedPhone = (phone: string) => {
  if (phone.startsWith("420")) {
    return parsePhoneNumber(`+${phone}`);
  }

  return parsePhoneNumber(phone);
};

const phoneCodeMapper = (country: Country) => ({
  value: `${country.value}`,
  label: `(${country.value}) ${country.telephoneCode}`,
});

interface MobilePhoneProps {
  mobileCountryCodeProps: FormInputProps;
  mobileProps: FormInputProps;
  disabled?: boolean;
  enableForeignNumbers?: boolean;
  noAutoComplete?: boolean;
  onSetMobilePhone: (value: string) => void;
  onSetMobileCountryCode: (value: string) => void;
}

const MobilePhone = ({
  mobileCountryCodeProps,
  mobileProps,
  disabled,
  enableForeignNumbers,
  noAutoComplete,
  onSetMobilePhone,
  onSetMobileCountryCode,
}: MobilePhoneProps) => {
  const enums = useContext(EnumerationsContext);

  const { t } = useTranslation("components");

  const mobilePhoneCodes = useMemo(() => {
    const cz = enums.countries.find(country => country.value === "CZ");
    const sk = enums.countries.find(country => country.value === "SK");
    const rest = enums.countries
      .filter(country => country.value !== "CZ" && country.value !== "SK")
      .sort((a, b) => a.telephoneCode.localeCompare(b.telephoneCode))
      .map(phoneCodeMapper);
    if (cz === undefined || sk === undefined) {
      return rest;
    }
    return [phoneCodeMapper(cz), phoneCodeMapper(sk), ...rest];
  }, [enums.countries]);

  const handlePasteMobilePhone = (event: React.ClipboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLTextAreaElement>) => {
    // prevent on accept
    event.preventDefault();

    const clipboardData = event.clipboardData.getData("Text");
    const parsedPhone = getParsedPhone(clipboardData);
    if (!parsedPhone) {
      // just copy it the way it is and do not set country code
      onSetMobilePhone(clipboardData);
      return;
    }

    const phoneCodesLabelRegex = new RegExp("(.+) \\+" + parsedPhone.countryCallingCode + "+$");
    const countryCode = mobilePhoneCodes.find(code => code.label.match(phoneCodesLabelRegex));

    onSetMobilePhone(parsedPhone.nationalNumber);
    onSetMobileCountryCode(countryCode?.value ?? "");
  };

  return (
    <InputWithSelect
      selectProps={{
        searchable: true,
        disabled: !(foreignAddressesFeatureFLag && enableForeignNumbers) || disabled,
        width: 130,
        data: mobilePhoneCodes,
        side: "left",
        "aria-label": t("MobilePhone.countryCodesLabel"),
        ...mobileCountryCodeProps,
      }}
      inputProps={{
        disabled,
        pii: true,
        id: "mobilePhone",
        name: "mobilePhone",
        autoComplete: noAutoComplete ? "off" : "tel-national",
        inputMode: "numeric",
        component: IMaskInput,
        mask: mobileCountryCodeProps.value === "CZ" ? "000 000 000" : "000000000000000",
        label: t("MobilePhone.label"),
        onAccept: (newMobilePhoneValue: string) => {
          onSetMobilePhone(newMobilePhoneValue.split(" ").join(""));
        },
        ...mobileProps,
        onChange: () => null,
        onPaste: handlePasteMobilePhone,
      }}
    />
  );
};

export default MobilePhone;
